import { Link } from 'react-router';
import {
  SEO,
  BreadcrumbSchema,
  ArticleSchema,
  FAQSchema,
  SpeakableSchema,
} from '@/components/seo/seo';
import { SmartCTA } from '@/components/ui/smart-cta';

interface BlogFAQ {
  question: string;
  answer: string;
}

interface BlogSource {
  name: string;
  url: string;
}

interface RelatedBlogPost {
  slug: string;
  title: string;
  description: string;
  readTime: string;
}

interface BlogReviewer {
  name: string;
  jobTitle: string;
  url?: string;
  worksFor?: {
    name: string;
    url?: string;
  };
  credentialCategory?: string;
  bio?: string;
}

interface BlogImage {
  src: string;
  alt: string;
  width: number;
  height: number;
  srcSet?: string;
  sizes?: string;
  caption?: string;
}

interface BlogPostLayoutProps {
  slug: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  datePublished: string;
  dateModified: string;
  keywords?: string[];
  geoFocus?: string[];
  atAGlance?: { label: string; value: string }[];
  faqs?: BlogFAQ[];
  sources?: BlogSource[];
  relatedPosts?: RelatedBlogPost[];
  reviewer?: BlogReviewer;
  image?: BlogImage;
  children: React.ReactNode;
}

function formatDisplayDate(date: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    // Date-only strings parse as UTC midnight; format in UTC so the
    // prerendered HTML matches the client in every timezone (hydration).
    timeZone: 'UTC',
  }).format(new Date(date));
}

export function BlogPostLayout({
  slug,
  title,
  description,
  category,
  readTime,
  datePublished,
  dateModified,
  keywords = [],
  geoFocus = [],
  atAGlance = [],
  faqs = [],
  sources = [],
  relatedPosts = [],
  reviewer,
  image,
  children,
}: BlogPostLayoutProps) {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: title, url: `/blog/${slug}` },
  ];

  return (
    <div className="relative">
      <SEO
        title={`${title} | Fixtyer Blog`}
        description={description}
        canonical={`/blog/${slug}`}
        type="article"
        image={image?.src}
        imageAlt={image?.alt}
        imageWidth={image?.width}
        imageHeight={image?.height}
        article={{
          publishedTime: datePublished,
          modifiedTime: dateModified,
          author: 'Fixtyer Team',
        }}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <ArticleSchema
        title={title}
        description={description}
        url={`/blog/${slug}`}
        image={image?.src}
        imageAlt={image?.alt}
        imageWidth={image?.width}
        imageHeight={image?.height}
        datePublished={datePublished}
        dateModified={dateModified}
        reviewedBy={reviewer}
      />
      <SpeakableSchema
        url={`/blog/${slug}`}
        headline={title}
        cssSelectors={['[data-speakable="article-summary"]', '[data-speakable="article-answer"]']}
      />
      {faqs.length > 0 && <FAQSchema faqs={faqs} />}

      <div className="bg-white border-b border-surface-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-brand-600">
            <Link to="/blog" className="hover:text-brand-700 transition-colors">
              Blog
            </Link>
            <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-600 font-medium">{title}</span>
          </div>
        </div>
      </div>

      <article>
        <header className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-12">
          <div
            className="absolute top-0 left-0 right-0 h-8"
            style={{
              background: `repeating-linear-gradient(
                -45deg,
                #f97316,
                #f97316 40px,
                #1e40af 40px,
                #1e40af 80px
              )`,
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
              {category}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-950 leading-tight mb-6">
              {title}
            </h1>
            <p data-speakable="article-summary" className="text-lg text-surface-800 max-w-3xl">
              {description}
            </p>
            {image && (
              <figure className="mt-8 overflow-hidden rounded-3xl border border-surface-200 bg-white shadow-xl shadow-brand-900/10">
                <img
                  src={image.src}
                  srcSet={image.srcSet}
                  sizes={image.sizes}
                  alt={image.alt}
                  width={image.width}
                  height={image.height}
                  loading="eager"
                  decoding="async"
                  className="w-full h-auto object-cover"
                />
                {image.caption && (
                  <figcaption className="px-5 py-3 text-sm text-surface-600 bg-white">
                    {image.caption}
                  </figcaption>
                )}
              </figure>
            )}
          </div>
        </header>

        <section className="bg-white border-b border-surface-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-surface-600">
              <span>{readTime} read</span>
              <span>Updated {formatDisplayDate(dateModified)}</span>
              {geoFocus.length > 0 && <span>Coverage: {geoFocus.join(', ')}</span>}
              {reviewer && <span>Reviewed by {reviewer.name}</span>}
            </div>
          </div>
        </section>

        {(atAGlance.length > 0 || keywords.length > 0) && (
          <section className="bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
              <div data-speakable="article-answer" className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
                <h2 className="font-display text-lg font-bold text-brand-950 mb-4">At a glance</h2>
                {atAGlance.length > 0 && (
                  <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {atAGlance.map((fact) => (
                      <div key={fact.label}>
                        <dt className="text-sm font-semibold text-brand-700">{fact.label}</dt>
                        <dd className="text-surface-700">{fact.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {keywords.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {keywords.map((keyword) => (
                      <span key={keyword} className="px-3 py-1 bg-white rounded-full text-xs font-medium text-brand-700 border border-brand-200">
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="py-10 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="blog-content">{children}</div>

            {reviewer && (
              <div className="mt-10 rounded-2xl border border-brand-200 bg-brand-50 p-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-brand-600 mb-2">
                  Reviewed by
                </p>
                <h2 className="font-display text-xl font-bold text-brand-950 mb-2">
                  {reviewer.url ? (
                    <a href={reviewer.url} target="_blank" rel="noopener noreferrer" className="hover:text-brand-700">
                      {reviewer.name}
                    </a>
                  ) : (
                    reviewer.name
                  )}
                </h2>
                <p className="text-sm text-brand-800 mb-3">
                  {reviewer.jobTitle}
                  {reviewer.worksFor && (
                    <>
                      {' '}
                      at{' '}
                      {reviewer.worksFor.url ? (
                        <a href={reviewer.worksFor.url} target="_blank" rel="noopener noreferrer" className="font-medium underline decoration-brand-300 underline-offset-2">
                          {reviewer.worksFor.name}
                        </a>
                      ) : (
                        reviewer.worksFor.name
                      )}
                    </>
                  )}
                </p>
                {reviewer.bio && <p className="text-sm text-surface-700 leading-relaxed">{reviewer.bio}</p>}
              </div>
            )}

            {sources.length > 0 && (
              <div className="mt-10 rounded-2xl border border-surface-200 bg-surface-50 p-6">
                <h2 className="font-display text-lg font-bold text-brand-950 mb-4">Sources</h2>
                <ul className="space-y-2">
                  {sources.map((source) => (
                    <li key={source.url}>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2"
                      >
                        {source.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      </article>

      {faqs.length > 0 && (
        <section className="py-12 bg-surface-50 border-t border-surface-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.question} className="bg-white rounded-xl border border-surface-200 p-6">
                  <h3 className="font-display font-semibold text-brand-950 mb-2">{faq.question}</h3>
                  <p className="text-surface-700 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white border-t border-surface-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-xl font-bold text-brand-950 mb-6">Related Blog Articles</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block bg-surface-50 rounded-xl border border-surface-200 p-5 hover:border-brand-300 hover:shadow-md transition-all"
                >
                  <h3 className="font-display font-semibold text-brand-950 mb-2 group-hover:text-brand-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-surface-700 mb-3">{post.description}</p>
                  <span className="text-sm text-surface-600">{post.readTime} read</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-white mb-2">
                Need help with a pothole claim?
              </h2>
              <p className="text-brand-300">
                Check your eligibility and build a clearer claim pack.
              </p>
            </div>
            <SmartCTA
              guestText="Start Free Check"
              className="inline-flex items-center justify-center px-6 py-3 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors whitespace-nowrap"
              showArrow
            />
          </div>
        </div>
      </section>
    </div>
  );
}
